import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';

const STATUS_LABEL = {
  pending: 'Gözlənilir', accepted: 'Qəbul edildi', courier_assigned: 'Kuryer təyin olundu',
  picked_up: 'Götürüldü', delivered: 'Çatdırıldı', rejected: 'Rədd edildi', cancelled: 'Ləğv edildi',
};

export default function DeliveryPage() {
  const { getAdminToken } = useAuth();
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [anomalies, setAnomalies] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.rpc('admin_get_delivery_orders', {
      p_admin_token: getAdminToken(),
      p_status: statusFilter === 'all' ? null : statusFilter,
    });
    setOrders(data?.success ? data.rows : []);
    setLoading(false);
  }, [statusFilter, getAdminToken]);

  const loadAnomalies = useCallback(async () => {
    const { data } = await supabase.rpc('admin_get_wallet_anomalies', { p_admin_token: getAdminToken() });
    setAnomalies(data?.success ? data.rows : []);
  }, [getAdminToken]);

  useEffect(() => { load(); loadAnomalies(); }, [load, loadAnomalies]);

  const cancelOrder = async (orderId) => {
    const reason = window.prompt('Ləğv səbəbi (kuryer yoxa çıxdı və s.):', 'Asılı sifariş — kuryer cavab vermir');
    if (reason === null) return;
    const { data, error } = await supabase.rpc('cancel_stuck_delivery_order', {
      p_order_id: orderId, p_reason: reason, p_idempotency_key: `cancel-${orderId}-${Date.now()}`,
      p_admin_token: getAdminToken(),
    });
    if (error) { alert('Xəta: ' + error.message); return; }
    if (!data?.success) { alert('Xəta: ' + (data?.error || 'Naməlum xəta')); return; }
    alert('Sifariş ləğv edildi, ödəniş geri qaytarıldı (əgər korporativ idisə).');
    load();
  };

  const delivered = orders.filter(o => o.status === 'delivered');
  const poolOrders = delivered.filter(o => o.courier_type === 'platform_pool');
  const totalRevenue = delivered.reduce((s, o) => s + Number(o.delivery_fee || 0), 0);
  const totalCommission = poolOrders.reduce((s, o) => s + Number(o.delivery_fee || 0) * 0.15, 0); // default 15% — real faiz business_locations-dan gəlir

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>🚴 Çatdırılma Monitorinqi</h1>
      <p style={{ fontSize: 13, color: '#888', marginBottom: 20 }}>Bütün kafelər üzrə çatdırılma sifarişləri</p>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}>
        <StatCard label="Cəmi sifariş" value={orders.length} />
        <StatCard label="Çatdırıldı" value={delivered.length} />
        <StatCard label="Çatdırılma haqqı dövriyyəsi" value={`${totalRevenue.toFixed(2)} ₼`} />
        <StatCard label="Hovuz komissiyası (~15%)" value={`${totalCommission.toFixed(2)} ₼`} />
      </div>

      {anomalies.length > 0 && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 12, padding: 16, marginBottom: 20 }}>
          <div style={{ fontWeight: 800, color: '#92400e', marginBottom: 10 }}>
            ⚠️ Qeyri-adi Günlük Xərcləmə ({anomalies.length})
          </div>
          <p style={{ fontSize: 12, color: '#92400e', marginBottom: 10 }}>
            Bu istifadəçilər bir gündə aylıq limitin 70%-dən çoxunu xərcləyib. Əməliyyatlar bloklanmayıb, sadəcə diqqətə çatdırılır.
          </p>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={th}>Tarix</th>
                <th style={th}>İstifadəçi</th>
                <th style={th}>Bu əməliyyat</th>
                <th style={th}>Günlük cəmi</th>
                <th style={th}>Aylıq limit</th>
              </tr>
            </thead>
            <tbody>
              {anomalies.map(a => (
                <tr key={a.id} style={{ borderTop: '1px solid #fed7aa' }}>
                  <td style={td}>{new Date(a.created_at).toLocaleString('az-AZ')}</td>
                  <td style={td}>{a.full_name || a.user_id}</td>
                  <td style={td}>{Number(a.amount).toFixed(2)} ₼</td>
                  <td style={td}>{Number(a.daily_total).toFixed(2)} ₼</td>
                  <td style={td}>{Number(a.monthly_limit).toFixed(2)} ₼</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #ddd' }}>
          <option value="all">Bütün statuslar</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {loading ? (
        <p>Yüklənir...</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden' }}>
          <thead>
            <tr style={{ background: '#f8f9fb', textAlign: 'left' }}>
              <th style={th}>Tarix</th>
              <th style={th}>Ünvan</th>
              <th style={th}>Cəmi</th>
              <th style={th}>Çatdırılma haqqı</th>
              <th style={th}>Ödəniş</th>
              <th style={th}>Kuryer tipi</th>
              <th style={th}>Status</th>
              <th style={th}></th>
            </tr>
          </thead>
          <tbody>
            {orders.map(o => {
              const isFinal = ['delivered', 'rejected', 'cancelled'].includes(o.status);
              return (
                <tr key={o.id} style={{ borderTop: '1px solid #f0f0f0' }}>
                  <td style={td}>{new Date(o.created_at).toLocaleString('az-AZ')}</td>
                  <td style={td}>{o.delivery_address}</td>
                  <td style={td}>{(Number(o.subtotal) + Number(o.delivery_fee)).toFixed(2)} ₼</td>
                  <td style={td}>{Number(o.delivery_fee).toFixed(2)} ₼</td>
                  <td style={td}>{o.payment_method === 'corporate_wallet' ? 'Korporativ' : 'Nağd'}</td>
                  <td style={td}>{o.courier_type === 'platform_pool' ? 'Hovuz' : o.courier_type === 'cafe_employed' ? 'Öz kuryer' : '—'}</td>
                  <td style={td}>{STATUS_LABEL[o.status] || o.status}</td>
                  <td style={td}>
                    {!isFinal && (
                      <button onClick={() => cancelOrder(o.id)} style={{
                        padding: '6px 12px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2',
                        color: '#dc2626', fontSize: 12, cursor: 'pointer',
                      }}>
                        Ləğv Et və Geri Qaytar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', minWidth: 160 }}>
      <div style={{ fontSize: 12, color: '#888' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e', marginTop: 4 }}>{value}</div>
    </div>
  );
}

const th = { padding: '12px 16px', fontSize: 12, fontWeight: 700, color: '#666' };
const td = { padding: '12px 16px', fontSize: 13, color: '#333' };
